import { useMemo } from 'react';

import { TFunction } from 'i18next';
import { CircleHelpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Brand from '@/components/atoms/Icons/Navigation/Brand';
import { PickerCombobox, PickerGroup } from '@/components/ui/custom/PickerCombobox';
import { CatalogBuckets } from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import { DEVELOPERS_SETTINGS_PATHS } from '@/modules/developerTools/constants';
import {
  modelIconResolver,
  providerNameMapper,
  UNKNOWN_PROVIDER_LABEL,
} from '@/modules/developerTools/pages/languageModels/helper';
import { ProviderName } from '@/modules/developerTools/types';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  ModelCatalogEntry,
  ModelStrategyStep,
  stepSelectorKey,
} from '@/shared/types/modelStrategy';

const PROXY_VALUE_PREFIX = 'proxy::';
const EXTERNAL_VALUE_PREFIX = 'external::';

// Friendly product-brand overrides keyed by wire alias. Static i18n keys
// (the alias→key map is constant, not built from the alias at runtime).
const ALIAS_LABEL_KEYS: Record<string, string> = {
  base: 'ai_agents.model_strategy.picker.aliases.base',
  'moveo-maestro': 'ai_agents.model_strategy.picker.aliases.moveo_maestro',
};

/** Wire alias stays as-is; user-facing label is translated when we have a brand override. */
export const aliasLabel = (alias: string, t: TFunction) => {
  const key = ALIAS_LABEL_KEYS[alias];
  return key ? t(key) : alias;
};

/** Friendly provider name (e.g. `moveo-ai` → "Moveo.AI", `openai` → "OpenAI"); unrecognized providers are localized as "Unknown". */
export const prettyProvider = (
  provider: string | null | undefined,
  t: TFunction
): string | null => {
  if (!provider) {
    return null;
  }
  const mapped = providerNameMapper(provider as ProviderName);
  return mapped === UNKNOWN_PROVIDER_LABEL
    ? t('ai_agents.model_strategy.picker.provider_unknown')
    : mapped;
};

/** Provider logo; Moveo-built models keep the brand mark, unrecognized providers get a neutral glyph. */
export const providerIcon = (provider: string | null | undefined) => {
  switch (provider) {
    case 'openai':
    case 'anthropic':
    case 'azure':
    case 'gemini':
    case 'vertex_ai':
    case 'deepseek':
      return modelIconResolver(provider, 16);
    case 'moveo':
    case 'moveo-ai':
      return <Brand size={16} color="var(--primary)" className="size-4 shrink-0" />;
    default:
      return <CircleHelpIcon className="size-4 shrink-0 text-muted-foreground" />;
  }
};

/** "Provider · Model" one-liner, or just the model name when the provider is absent. */
const modelLabel = (provider: string | null | undefined, name: string, t: TFunction): string => {
  const prettyName = prettyProvider(provider, t);
  return prettyName ? `${prettyName} · ${name}` : name;
};

type StepSelector = Pick<ModelStrategyStep, 'language_model_id' | 'language_model_alias'>;

const toSelector = (itemValue: string): StepSelector =>
  itemValue.startsWith(EXTERNAL_VALUE_PREFIX)
    ? {
        language_model_id: itemValue.slice(EXTERNAL_VALUE_PREFIX.length),
        language_model_alias: null,
      }
    : { language_model_id: null, language_model_alias: itemValue.slice(PROXY_VALUE_PREFIX.length) };

type ModelPickerProps = {
  value: ModelStrategyStep;
  buckets: CatalogBuckets;
  onChange: (next: StepSelector) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  /** Forwarded to PopoverContent. Use to bump z-index above modals (e.g. `z-3000`). */
  contentClassName?: string;
  /** Trigger text when no model is selected. Defaults to "Select model". */
  placeholder?: string;
  /** Selector keys already used by other steps in the same chain — rendered disabled to enforce uniqueness. */
  usedKeys?: ReadonlySet<string>;
};

/**
 * Bucket-grouped model picker for a single model_strategy step. Emits the
 * canonical step shape: at most one of `language_model_id` /
 * `language_model_alias` is non-null. A both-null step is the unconfigured
 * default and renders as a "Select model" placeholder (see MOV-16940).
 */
export const ModelPicker = ({
  value,
  buckets,
  onChange,
  disabled,
  error,
  className,
  contentClassName,
  placeholder,
  usedKeys,
}: ModelPickerProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const selectedValue = useMemo(() => {
    if (value.language_model_id) {
      return `${EXTERNAL_VALUE_PREFIX}${value.language_model_id}`;
    }
    if (value.language_model_alias) {
      return `${PROXY_VALUE_PREFIX}${value.language_model_alias}`;
    }
    return null;
  }, [value.language_model_id, value.language_model_alias]);

  const triggerLabel = useMemo<{
    name: string;
    provider: string | null;
    isDefault?: boolean;
  }>(() => {
    if (value.language_model_id) {
      const match = buckets.external.find((m) => m.language_model_id === value.language_model_id);
      return match
        ? { name: aliasLabel(match.alias, t), provider: match.provider }
        : { name: t('ai_agents.model_strategy.picker.unknown_byollm'), provider: null };
    }
    if (value.language_model_alias) {
      const match = [...buckets.internal, ...buckets.proxy].find(
        (m) => m.alias === value.language_model_alias
      );
      return { name: aliasLabel(value.language_model_alias, t), provider: match?.provider ?? null };
    }
    // Both-null = no model strategy set yet. Show a neutral "Select model"
    // placeholder (no provider icon, no model name): unconfigured brains route
    // through the legacy path at runtime (GPT-4o, not Maestro), and that
    // fallback is intentionally not surfaced. See MOV-16940.
    return {
      name: placeholder ?? t('ai_agents.model_strategy.picker.moveo_default'),
      provider: null,
      isDefault: true,
    };
  }, [
    value.language_model_id,
    value.language_model_alias,
    buckets.external,
    buckets.internal,
    buckets.proxy,
    placeholder,
    t,
  ]);

  const toOption = (entry: ModelCatalogEntry) => {
    const isExternal = entry.bucket === 'external' && entry.language_model_id;
    const itemValue = isExternal
      ? `${EXTERNAL_VALUE_PREFIX}${entry.language_model_id}`
      : `${PROXY_VALUE_PREFIX}${entry.alias}`;
    const selector = toSelector(itemValue);
    // Disable models already chosen by another step in this chain (orchestrator
    // rejects duplicate steps), but never disable this row's own current value.
    const isUsedElsewhere =
      itemValue !== selectedValue && !!usedKeys?.has(stepSelectorKey(selector));
    return {
      value: itemValue,
      label: modelLabel(entry.provider, aliasLabel(entry.alias, t), t),
      icon: providerIcon(entry.provider),
      disabled: isUsedElsewhere,
    };
  };

  const groups: PickerGroup[] = [
    {
      key: 'internal',
      heading: t('ai_agents.model_strategy.picker.moveo_default_group'),
      options: buckets.internal.map(toOption),
    },
    {
      key: 'proxy',
      heading: t('ai_agents.model_strategy.picker.moveo_hosted_group'),
      options: buckets.proxy.map(toOption),
    },
    {
      key: 'external',
      heading: t('ai_agents.model_strategy.picker.byollm_group'),
      options: buckets.external.map(toOption),
    },
  ];

  return (
    <PickerCombobox
      groups={groups}
      value={selectedValue}
      onSelect={(next) => onChange(toSelector(next))}
      footer={{
        to: `/${slug}${DEVELOPERS_SETTINGS_PATHS.llms}`,
        label: t('ai_agents.model_strategy.picker.add_new'),
      }}
      disabled={disabled}
      error={error}
      className={className}
      contentClassName={contentClassName}
    >
      <span className="flex items-center gap-2 truncate">
        {!triggerLabel.isDefault && providerIcon(triggerLabel.provider)}
        <span className="truncate">
          {triggerLabel.isDefault
            ? triggerLabel.name
            : modelLabel(triggerLabel.provider, triggerLabel.name, t)}
        </span>
      </span>
    </PickerCombobox>
  );
};
